import type { ProjectRole } from "hooks/project/project.types";

export type ParsedProjectPermissions = {
  permissions: string[];
  workspaceId: string | null;
  projectRole: ProjectRole | string | null;
  workspaceRole: string | null;
  projectInfo: {
    isLicensed: boolean;
    isTrialProject: boolean;
    freeLicensePremiumLocked: boolean;
    unlicensedMaxDraftScenes?: number;
    createSource?: string;
  };
};

const readRecord = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === "object" ? (value as Record<string, unknown>) : null;

const readStringArray = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((entry): entry is string => typeof entry === "string") : [];

/**
 * Parses the canonical `permissions` embed from GET /projects/:id (and matching /me project rows).
 * This is the only place frontend code should interpret that shape.
 */
export const parseProjectPermissions = (
  projectPayload: unknown,
  _fallbackProjectId?: string | number | null
): ParsedProjectPermissions | null => {
  const project = readRecord(projectPayload);
  if (!project) {
    return null;
  }

  const permissionsRoot = readRecord(project.permissions);
  if (!permissionsRoot) {
    return null;
  }

  const permissionList = readStringArray(permissionsRoot.permissions);
  if (permissionList.length === 0) {
    return null;
  }

  const roles = readRecord(permissionsRoot.roles);
  const projectInfoRaw = readRecord(permissionsRoot.projectInfo ?? permissionsRoot.project_info);
  const projectInfoRow = projectInfoRaw ?? {};

  const workspaceIdRaw = project.workspace_id ?? project.workspaceId;
  const workspaceId =
    workspaceIdRaw != null && String(workspaceIdRaw).trim() !== "" ? String(workspaceIdRaw) : null;

  const isTrialProject = Boolean(projectInfoRow.isTrialProject ?? projectInfoRow.is_trial_project);
  const freeLicensePremiumLocked = Boolean(
    projectInfoRow.freeLicensePremiumLocked ?? projectInfoRow.free_license_premium_locked
  );
  const isLicensed =
    typeof projectInfoRow.isLicensed === "boolean"
      ? Boolean(projectInfoRow.isLicensed)
      : typeof projectInfoRow.is_licensed === "boolean"
        ? Boolean(projectInfoRow.is_licensed)
        : !(isTrialProject || freeLicensePremiumLocked);

  const rawMax =
    projectInfoRow.unlicensedMaxDraftScenes ?? projectInfoRow.unlicensed_max_draft_scenes;
  const unlicensedMaxDraftScenes =
    typeof rawMax === "number" && Number.isFinite(rawMax) && rawMax > 0 ? rawMax : undefined;

  const createSourceRaw = projectInfoRow.createSource ?? projectInfoRow.create_source;
  const rootCreateSource = project.create_source;
  const createSource =
    typeof createSourceRaw === "string" && createSourceRaw.trim() !== ""
      ? createSourceRaw.trim()
      : typeof rootCreateSource === "string" && rootCreateSource.trim() !== ""
        ? rootCreateSource.trim()
        : undefined;

  const projectRoleRaw = roles?.projectRole ?? roles?.project_role;
  const workspaceRoleRaw = roles?.workspaceRole ?? roles?.workspace_role;

  return {
    permissions: permissionList,
    workspaceId,
    projectRole:
      typeof projectRoleRaw === "string" && projectRoleRaw.trim() !== "" ? projectRoleRaw : null,
    workspaceRole:
      typeof workspaceRoleRaw === "string" && workspaceRoleRaw.trim() !== ""
        ? workspaceRoleRaw
        : null,
    projectInfo: {
      isLicensed,
      isTrialProject,
      freeLicensePremiumLocked,
      unlicensedMaxDraftScenes,
      createSource,
    },
  };
};

export const findCachedProjectPayload = (
  getQueriesData: (filters: { queryKey: readonly unknown[] }) => [readonly unknown[], unknown][],
  projectId: string
): unknown => {
  const entries = getQueriesData({ queryKey: ["projects", "detail", projectId] });
  for (let index = entries.length - 1; index >= 0; index -= 1) {
    const [, data] = entries[index];
    if (data) {
      return data;
    }
  }
  return null;
};
